/**
 * Синхронизация встреч с календарём устройства.
 *
 * Здесь план сверки (`merge.ts`) встречается с базой и с плагином. Правило
 * одно: встреча, пришедшая из календаря устройства, — обычная встреча
 * приложения. Её можно править, двигать в сетке дня и удалять наравне с
 * заведёнными здесь, и любая такая правка уезжает обратно.
 *
 * ## Окно
 *
 * Сверяется не вся история, а окно вокруг сегодняшнего дня: месяц назад и три
 * месяца вперёд. Прошлое дальше месяца никто не правит, а будущее дальше
 * квартала ещё не наступило — читать календарь целиком значило бы платить за
 * это временем запуска каждый раз.
 *
 * ## Отпечаток
 *
 * После каждого удачного действия у встречи обновляется отпечаток —
 * состояние, на котором стороны сошлись. Именно он позволяет следующей сверке
 * понять, кто менялся. Забыть его обновить страшнее, чем не синхронизировать
 * вовсе: сверка начнёт считать изменившимися обе стороны и при каждом запуске
 * будет решать несуществующий спор.
 */

import { eventsRepo } from '@/entities/event/model/repository';
import { tasksRepo } from '@/entities/task/model/repository';
import { db } from '@/shared/db/database';
import { addDays, type IsoDate, momentAt, today as todayIso } from '@/shared/lib/datetime';

import { clearAllDayItems, expandAllDay, writeAllDayItems } from './all-day';
import { type ContactBirthday, type ContactsPort, systemContacts } from './contacts';
import {
  fieldsOfExternal,
  fingerprint,
  isInstanceId,
  itemOfEvent,
  itemOfTask,
  planCalendarSync,
  type SyncAction,
  type SyncItem,
} from './merge';
import {
  readCalendarSyncSettings,
  writeCalendarSyncState,
  type CalendarSyncSettings,
} from './settings';
import { syncReminders } from './sync-reminders';
import { systemCalendar, type SystemCalendarPort } from './system-calendar';

/** Сколько дней назад и вперёд сверяется календарь. */
export const SYNC_PAST_DAYS = 30;
export const SYNC_AHEAD_DAYS = 90;

export interface SyncReport {
  /** Сколько встреч пришло из календаря устройства. */
  pulled: number;
  /** Сколько уехало туда. */
  pushed: number;
  /** Сколько удалено по обе стороны. */
  removed: number;
  /** Сколько полей пришлось разрешать в пользу календаря устройства. */
  conflicts: number;
  /** Сколько удалений отложено: разом пропало слишком много — похоже на сбой. */
  held: number;
}

export const EMPTY_REPORT: SyncReport = {
  pulled: 0,
  pushed: 0,
  removed: 0,
  conflicts: 0,
  held: 0,
};

/**
 * Сверяет встречи с календарём устройства.
 *
 * Возвращает `null`, когда сверять нечего: синхронизация выключена, доступа
 * нет или не выбран ни один календарь. Это не ошибка — это обычное состояние
 * до настройки.
 */
export async function syncSystemCalendar(
  userId: string,
  options: { port?: SystemCalendarPort; contacts?: ContactsPort; today?: IsoDate } = {},
): Promise<SyncReport | null> {
  const port = options.port ?? systemCalendar();
  const today = options.today ?? todayIso();

  const settings = await readCalendarSyncSettings(userId);
  if (!settings.enabled || settings.sources.length === 0) {
    // Синхронизацию выключили — дни рождения из чужого календаря больше не
    // показываются: читать его человек больше не разрешает.
    await clearAllDayItems(userId);
    return null;
  }
  if (!port.available() || (await port.checkAccess()) !== 'granted') return null;

  const from = addDays(today, -SYNC_PAST_DAYS);
  const to = addDays(today, SYNC_AHEAD_DAYS);

  // События на весь день встречами не становятся: им нет места в сетке дня,
  // и они показываются строкой над ним. Окно пересобирается целиком — так
  // уходит и удалённое в телефоне, и снятое с чтения.
  const [allDay, calendars, birthdays] = await Promise.all([
    port.listAllDayEvents(momentAt(from, 0), momentAt(to, 24 * 60 - 1), settings.sources),
    port.listCalendars(),
    readBirthdays(options.contacts ?? systemContacts()),
  ]);
  await writeAllDayItems(userId, expandAllDay(allDay, calendars, from, to, birthdays));

  /*
    Читаются выбранные календари и тот, куда уходят наши встречи, — но только
    те, что в телефоне действительно есть. Записи остальных сверка не судит:
    их событий нет в списке не потому, что их удалили.
  */
  const push = settings.push && settings.target !== null;
  const existing = new Set(calendars.map((calendar) => calendar.id));
  const readCalendars = [
    ...new Set([...settings.sources, ...(push && settings.target ? [settings.target] : [])]),
  ].filter((id) => existing.has(id));

  const local = [
    ...(await eventsInWindow(userId, from, to)).map(itemOfEvent),
    ...(await tasksInWindow(userId, from, to)).map(itemOfTask),
  ];
  const external = await port.listEvents(
    momentAt(from, 0),
    momentAt(to, 24 * 60 - 1),
    readCalendars,
  );

  const { actions, conflicts, held } = planCalendarSync({
    local,
    external,
    push,
    readCalendars,
    sources: settings.sources,
  });

  const report = { ...EMPTY_REPORT, conflicts, held };
  for (const action of actions) await apply(action, settings, port, report);

  await writeCalendarSyncState(userId, { at: Date.now(), held });
  return report;
}

/**
 * Дни рождения из контактов — ради возраста. Без доступа к контактам их нет,
 * и это не ошибка: возраст просто не показывается.
 */
async function readBirthdays(contacts: ContactsPort): Promise<ContactBirthday[]> {
  if (!contacts.available()) return [];
  try {
    if ((await contacts.checkAccess()) !== 'granted') return [];
    return await contacts.listBirthdays();
  } catch {
    return [];
  }
}

/** Не чаще раза в пять минут: возврат в приложение бывает частым. */
const RESUME_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Сверка при открытии приложения и при каждом возвращении в него.
 *
 * Возвращение важнее самого запуска: человек принимает приглашение в почте и
 * переключается обратно — встреча должна оказаться в дне уже здесь, а не после
 * перезапуска. Частые переключения между приложениями от этого не страдают:
 * между сверками выдерживается интервал.
 *
 * Возвращает функцию отписки.
 */
export function watchCalendarSync(userId: string): () => void {
  let last = 0;
  let running = false;

  const run = async () => {
    if (running || Date.now() - last < RESUME_INTERVAL_MS) return;

    running = true;
    try {
      // Напоминания — отдельное хранилище, и их неудача не должна отменять
      // сверку календаря.
      await Promise.allSettled([syncSystemCalendar(userId), syncReminders(userId)]);
      last = Date.now();
    } catch {
      // Молча: сверка — фоновая задача, и всплывающее сообщение о ней при
      // каждом возврате в приложение раздражало бы сильнее, чем помогало.
    } finally {
      running = false;
    }
  };

  const onVisible = () => {
    if (document.visibilityState === 'visible') void run();
  };

  void run();
  document.addEventListener('visibilitychange', onVisible);

  return () => document.removeEventListener('visibilitychange', onVisible);
}

/**
 * Встречи окна, включая мягко удалённые.
 *
 * Удалённые нужны затем, что именно они говорят сверке: «эту встречу человек
 * выбросил, убери её и в календаре устройства». Без них удаление здесь просто
 * возвращалось бы обратно при следующей сверке.
 */
async function eventsInWindow(userId: string, from: IsoDate, to: IsoDate) {
  return db.events.where('[userId+date]').between([userId, from], [userId, to], true, true).toArray();
}

/**
 * Задачи окна — те, что стоят на времени, плюс те, что связь уже имеют.
 *
 * Второе важнее первого. Задача, у которой убрали время или которую удалили,
 * перестаёт быть записью календаря — и если не принести её сюда, занятое
 * время останется в общем календаре навсегда, а коллеги будут видеть встречу,
 * которой нет.
 */
async function tasksInWindow(userId: string, from: IsoDate, to: IsoDate) {
  const planned = await db.tasks
    .where('[userId+plannedDate]')
    .between([userId, from], [userId, to], true, true)
    .toArray();

  const linked = await db.tasks.where('externalId').notEqual('').toArray();
  const seen = new Set(planned.map((task) => task.id));

  return [
    ...planned,
    ...linked.filter((task) => task.userId === userId && !seen.has(task.id)),
  ];
}

/**
 * У события из календаря уже есть пара здесь — просто вне сверяемого окна.
 *
 * Ищется по индексу в обеих таблицах: пара могла быть и встречей, и задачей.
 */
async function hasCounterpart(externalId: string): Promise<boolean> {
  const [event, task] = await Promise.all([
    db.events.where('externalId').equals(externalId).first(),
    db.tasks.where('externalId').equals(externalId).first(),
  ]);

  return event !== undefined || task !== undefined;
}

/** Записывает связь с календарём туда, откуда запись пришла. */
async function linkLocal(
  item: SyncItem,
  patch: { externalId?: string | null; externalCalendarId?: string | null; syncedHash: string | null },
): Promise<void> {
  if (item.kind === 'task') await tasksRepo.update(item.id, patch);
  else await eventsRepo.update(item.id, patch);
}

async function apply(
  action: SyncAction,
  settings: CalendarSyncSettings,
  port: SystemCalendarPort,
  report: SyncReport,
): Promise<void> {
  try {
    switch (action.kind) {
      case 'createLocal': {
        const fields = fieldsOfExternal(action.external);

        /*
          Защита от двойника. Запись, передвинутая за пределы сверяемого окна,
          из списка местных пропадает, а её пара в календаре остаётся — и без
          этой проверки вернулась бы сюда вторым экземпляром. Задвоение данных
          для синхронизации худший исход: человек не знает, какая из двух
          записей настоящая.
        */
        if (await hasCounterpart(action.external.id)) return;

        await eventsRepo.create({
          title: fields.title,
          notes: fields.notes,
          sphereId: settings.sphereId,
          date: fields.date,
          startMin: fields.startMin,
          durationMin: fields.durationMin,
          location: fields.location,
          remindBeforeMin: null,
          externalId: action.external.id,
          externalCalendarId: action.external.calendarId,
          syncedHash: fingerprint(fields),
        });
        report.pulled += 1;
        return;
      }

      case 'updateLocal': {
        const link = {
          // Номер переписывается: прежняя копия серии получает номер своего дня.
          externalId: action.external.id,
          externalCalendarId: action.external.calendarId,
          syncedHash: fingerprint(action.fields),
        };

        if (action.local.kind === 'task') {
          // Задача принимает время и название, но остаётся задачей: её
          // по-прежнему можно закрыть флажком, и она считается в бюджете дня.
          await tasksRepo.update(action.local.id, {
            title: action.fields.title,
            notes: action.fields.notes,
            plannedDate: action.fields.date,
            plannedStartMin: action.fields.startMin,
            estimateMin: action.fields.durationMin,
            ...link,
          });
        } else {
          await eventsRepo.update(action.local.id, { ...action.fields, ...link });
        }

        report.pulled += 1;
        return;
      }

      case 'createExternal': {
        if (settings.target === null) return;

        const fields = action.local.fields;
        const id = await port.createEvent(settings.target, fields);

        await linkLocal(action.local, {
          externalId: id,
          externalCalendarId: settings.target,
          syncedHash: fingerprint(fields),
        });
        report.pushed += 1;
        return;
      }

      case 'updateExternal': {
        // День серии в телефоне не правится: изменилась бы вся серия.
        if (action.local.externalId === null || isInstanceId(action.local.externalId)) return;

        await port.updateEvent(action.local.externalId, action.fields);
        await linkLocal(action.local, { syncedHash: fingerprint(action.fields) });
        report.pushed += 1;
        return;
      }

      case 'deleteLocal': {
        /*
          Связь снимается вместе с удалением. Удалила не человек, а сверка —
          вслед за календарём устройства, — и если событие там вернётся
          (восстановили из корзины, снова отметили календарь), запись с
          живой связью стёрла бы его уже в телефоне.
        */
        await linkLocal(action.local, { externalId: null, syncedHash: null });
        if (action.local.kind === 'task') await tasksRepo.remove(action.local.id);
        else await eventsRepo.remove(action.local.id);

        report.removed += 1;
        return;
      }

      case 'deleteExternal': {
        // Удаление по номеру дня серии стёрло бы в телефоне всю серию.
        if (action.local.externalId === null || isInstanceId(action.local.externalId)) return;

        await port.deleteEvent(action.local.externalId);
        // Связь снимается: событие в календаре устройства уже стёрто, и
        // повторная попытка удалить его при каждой сверке ни к чему.
        await linkLocal(action.local, { externalId: null, syncedHash: null });
        report.removed += 1;
        return;
      }

      case 'markSynced': {
        await linkLocal(action.local, { syncedHash: fingerprint(action.fields) });
        return;
      }
    }
  } catch {
    /*
      Одно неудачное действие не должно ронять всю сверку: календарь мог
      удалить событие прямо сейчас, разрешение — отозваться, запись в
      подписной календарь — оказаться запрещённой. Остальные встречи от этого
      не страдают, а неудавшееся действие повторится при следующей сверке:
      отпечаток у него не обновлён.
    */
  }
}
